import { useAppSettingsStore } from '../stores/app-settings-store';
import type { DesktopEntry } from '../types/desktop-entry';
import type { LifecycleState } from '../types/lifecycle';
import { AppIcon } from './app-icon';
import { LifecycleBadge } from './window/LifecycleBadge';

interface DesktopIconProps {
	entry: DesktopEntry;
	onLaunch: () => void;
	isMobile?: boolean;
	/** Lifecycle state for the badge + click override. Falls back to
	 *  `entry.lifecycleState` so callers can attach state to the entry itself. */
	lifecycleState?: LifecycleState;
}

export function DesktopIcon({ entry, onLaunch, isMobile, lifecycleState }: DesktopIconProps) {
	const iconSize = isMobile ? 36 : 32;
	const state = lifecycleState ?? entry.lifecycleState;
	const openSettings = useAppSettingsStore((s) => s.openAppSettings);
	const isUninstalling = state === 'UNINSTALLING';

	// NEEDS_CONFIG icons can't launch — the pod isn't deployed yet. Route the
	// click into Settings -> Environment instead so the user can finish setup.
	const handleActivate = () => {
		if (state === 'NEEDS_CONFIG' || state === 'INSTALLED') {
			openSettings(entry.id, 'environment');
			return;
		}
		onLaunch();
	};

	return (
		<button
			className={
				isMobile
					? 'flex flex-col items-center gap-2 rounded-xl p-3 text-center transition-colors active:bg-[var(--khal-desktop-icon-hover)]'
					: 'flex w-20 flex-col items-center gap-1.5 rounded-lg p-2 text-center transition-colors hover:bg-[var(--khal-desktop-icon-hover)] focus-visible:bg-[var(--khal-desktop-icon-hover)]'
			}
			style={{ opacity: isUninstalling ? 0.5 : 1 }}
			onClick={isMobile ? handleActivate : undefined}
			onDoubleClick={isMobile ? undefined : handleActivate}
			onKeyDown={
				isMobile
					? undefined
					: (e) => {
							if (e.key === 'Enter' || e.key === ' ') {
								e.preventDefault();
								handleActivate();
							}
						}
			}
			aria-label={`Open ${entry.name}`}
			data-lifecycle-state={state}
		>
			<div
				className={
					isMobile
						? 'relative flex h-14 w-14 items-center justify-center drop-shadow-lg'
						: 'relative flex h-12 w-12 items-center justify-center drop-shadow-lg'
				}
				aria-hidden="true"
			>
				<AppIcon appId={entry.component || entry.id} size={iconSize} />
				{state && state !== 'RUNNING' && state !== 'INSTALLED' && (
					<span
						className="absolute"
						style={{ bottom: -2, right: -2 }}
						data-testid={`desktop-icon-badge-${entry.id}`}
					>
						<LifecycleBadge state={state} size={isMobile ? 12 : 10} />
					</span>
				)}
			</div>
			<span className="w-full truncate text-label-12 text-gray-1000 dark:text-gray-1000">{entry.name}</span>
		</button>
	);
}
